// frontend/src/utils/api.js
import axios from 'axios';
import { API_BASE } from './config';

/**
 * API Base URL Resolution Strategy:
 * 1. Runtime override (window.__API_BASE__) - Allows ops to override without rebuild
 * 2. Build-time env var (REACT_APP_API_BASE_URL) - Set during build
 * 3. Config file fallback (API_BASE from config.js)
 * 4. Relative path (/api) - For nginx/proxy deployments
 * 
 * This flexible approach supports multiple deployment scenarios:
 * - Local dev: http://localhost:4000
 * - Production: https://api.securethebag.ai
 * - Proxied: /api (nginx forwards to backend)
 * - Runtime override: window.__API_BASE__ = 'https://staging-api.example.com'
 */

// 1. Check for runtime override (useful for staging/testing without rebuild)
const runtimeBase = typeof window !== 'undefined' ? window.__API_BASE__ : undefined;

// 2. Build-time environment variable (CRA injects REACT_APP_* vars)
const buildBase = process.env.REACT_APP_API_BASE_URL;

// 3. Determine final base URL with proper fallback chain
const baseURL = runtimeBase || buildBase || API_BASE || '/api';

// Log the resolved base URL for debugging (dev only)
if (process.env.NODE_ENV === 'development') {
  console.log('[API Config] Resolved base URL:', baseURL, {
    runtimeBase,
    buildBase,
    configBase: API_BASE,
    fallback: '/api',
  });
}

const api = axios.create({
  baseURL,
  withCredentials: false,
});

// ============================================================================
// SECURE TOKEN STORAGE
// ============================================================================
// Instead of storing tokens in localStorage (vulnerable to XSS), we use:
// 1. In-memory storage (primary) - cleared on page refresh
// 2. sessionStorage (fallback) - cleared when tab closes, slightly more secure than localStorage
//
// Note: The most secure approach would be httpOnly cookies, but that requires
// significant architectural changes. This is a pragmatic middle ground.
// ============================================================================

let inMemoryToken = null; // Primary storage - most secure

const TokenStorage = {
  /**
   * Store access token securely
   * @param {string} token - JWT access token
   */
  setToken(token) {
    // Store in memory (primary)
    inMemoryToken = token;
    
    // Store in sessionStorage (fallback for page refresh)
    // sessionStorage is cleared when tab closes, unlike localStorage
    try {
      sessionStorage.setItem('accessToken', token);
    } catch (e) {
      console.warn('Failed to store token in sessionStorage:', e);
    }
  },

  /**
   * Retrieve access token
   * @returns {string|null} JWT access token
   */
  getToken() {
    // Try in-memory first (fastest, most secure)
    if (inMemoryToken) return inMemoryToken;
    
    // Fallback to sessionStorage
    try {
      const token = sessionStorage.getItem('accessToken');
      if (token) {
        inMemoryToken = token; // Restore to memory
        return token;
      }
    } catch (e) {
      console.warn('Failed to retrieve token from sessionStorage:', e);
    }
    
    // Legacy fallback to localStorage (for migration)
    try {
      const legacyToken = localStorage.getItem('accessToken');
      if (legacyToken) {
        console.warn('Found token in localStorage, migrating to sessionStorage');
        this.setToken(legacyToken);
        localStorage.removeItem('accessToken'); // Clean up
        return legacyToken;
      }
    } catch (e) {
      console.warn('Failed to check localStorage:', e);
    }
    
    return null;
  },

  /**
   * Remove access token
   */
  removeToken() {
    inMemoryToken = null;
    
    try {
      sessionStorage.removeItem('accessToken');
    } catch (e) {
      console.warn('Failed to remove token from sessionStorage:', e);
    }
    
    // Also clean up any legacy localStorage tokens
    try {
      localStorage.removeItem('accessToken');
    } catch (e) {
      console.warn('Failed to remove token from localStorage:', e);
    }
  },

  /**
   * Check if token exists
   * @returns {boolean}
   */
  hasToken() {
    return this.getToken() !== null;
  }
};

// Track token refresh to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

// Decode JWT to check expiry (simple base64 decode)
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch {
    return null;
  }
}

// Check if token will expire in the next 10 minutes
function shouldRefreshToken(token) {
  const expiry = getTokenExpiry(token);
  if (!expiry) return false;
  const now = Date.now();
  const tenMinutes = 10 * 60 * 1000;
  return (expiry - now) < tenMinutes;
}

// inject the JWT into every request and handle token refresh
api.interceptors.request.use(async cfg => {
  let token = TokenStorage.getToken();
  
  // Check if token needs refresh (expires in < 10 minutes)
  if (token && shouldRefreshToken(token)) {
    if (!isRefreshing) {
      isRefreshing = true;
      
      try {
        // Get CSRF token first
        const csrfResponse = await axios.get(`${baseURL}/auth/csrf-token`, { 
          withCredentials: true 
        });
        const csrfToken = csrfResponse.data.csrfToken;
        
        // Attempt to refresh the token
        const response = await axios.post(
          `${baseURL}/auth/refresh`,
          {},
          { 
            withCredentials: true,
            headers: { 'X-CSRF-Token': csrfToken }
          }
        );
        
        const newToken = response.data.accessToken;
        TokenStorage.setToken(newToken);
        token = newToken;
        
        // Notify all waiting requests
        onTokenRefreshed(newToken);
      } catch (error) {
        console.error('Token refresh failed:', error);

        // Refresh failed - clear tokens and redirect to login immediately
        TokenStorage.removeToken();
        refreshSubscribers = []; // Clear waiting requests

        const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?redirect=${returnTo}`;

        // Throw to prevent the current request from proceeding
        throw new Error('Session expired - redirecting to login');
      } finally {
        isRefreshing = false;
      }
    } else {
      // Another request is already refreshing, wait for it
      token = await new Promise(resolve => {
        subscribeTokenRefresh(newToken => {
          resolve(newToken);
        });
      });
    }
  }
  
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// catch any 401 (Unauthorized) response
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      // If we're already on the login page, let the error propagate normally
      // so the login form can display the error message
      const currentPath = window.location.pathname;
      if (currentPath === '/login' || currentPath === '/signup') {
        console.log('API.JS 401 on login/signup page - allowing error to propagate');
        return Promise.reject(err);
      }

      // Otherwise, clear the bad token and redirect to login
      console.log('API.JS 401 - Logging out, removing access token');
      TokenStorage.removeToken();
      // redirect to login, preserving current path
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?redirect=${returnTo}`;
      // return a pending promise so the original call never resolves
      return new Promise(() => {});
    }

    // ── 403: planner entitlement required ──────────────────────────
    // requirePlanner middleware returns: { error: 'Planner access required', plannerGated: true }
    // Only catch planner-specific 403s — other 403s (ownership checks, etc.) propagate normally
    if (err.response?.status === 403 && err.response?.data?.plannerGated && !err.config?._backgroundRequest) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/dashboard/billing?upgrade=planner&from=${returnTo}`;
      return new Promise(() => {});
    }

    return Promise.reject(err);
  }
);

// Export the api instance, TokenStorage, and resolved baseURL for use in components
export default api;
export { TokenStorage, baseURL };